import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Grid, GridCol, TabItem, TabPanel, Tabs } from '@flixbus/honeycomb-react';

import Loader from 'common/Loader';
import NoData from 'common/NoData';
import PageSizer from 'common/PageSizer';
import Pagination from 'common/Pagination';
import TimezoneSelector from 'common/TimezoneSelector';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { TIME_ZONES_ADDITIONS } from 'utils/datetime';

import { makeActiveElementsConfig, PAGE_KEY } from './activeElementsConfigFactory';
import DeadheadItem from './components/DeadheadItem';
import RideFilters from './components/RideFilters';
import { rideFilterStore } from './components/RideFilters/RideFilterStore';
import RideIdForm from './components/RideIdFilter';
import RideItem from './components/RideItem';
import { TABS_MODS } from './const';
import { rideListStore } from './RideListStore';

import styles from './styles.module.css';

const RideListPage = observer(() => {
	const active = useActiveElements();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		const mode = searchParams.get('mode');
		if (mode) {
			rideListStore.setSearchMode(mode);
			setSearchParams({}, { replace: true });
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const activeConfig = makeActiveElementsConfig();

		active.mountForPage(PAGE_KEY, activeConfig);
		return () => active.cleanupPage(PAGE_KEY);
	}, [active]);

	useEffect(() => {
		rideFilterStore.checkFilterOnMount();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		return () => {
			rideFilterStore.resetUrlParamsOnExit();
			rideFilterStore.setRideId('');
		};
	}, []);

	const {
		isRidesError,
		isRidesLoading,
		rides,
		activePage,
		pageCount,
		fetchRides,
		// fetchDeadheadByIdWithFallback,
		searchMode,
		selectedTimezone,
		tabsMods,
		setSearchMode,
		setSelectedTimezone,
		setActivePage,
		isDeadheadsTabActive,
		// isShowingFallbackRides,
	} = rideListStore;

	const { filterObject, isRideIdApplied, deadheadId, filtersLoaded } = rideFilterStore;

	useEffect(() => {
		if (!filtersLoaded) return;
		// TODO: CLARIFY DEADHEAD SEARCH
		// if (isDeadheadsTabActive && deadheadId) {
		// 	fetchDeadheadByIdWithFallback(deadheadId);
		// } else {
		//
		// }
		fetchRides(filterObject, isRideIdApplied);
	}, [
		searchMode,
		activePage,
		filterObject,
		isDeadheadsTabActive,
		deadheadId,
		isRideIdApplied,
		fetchRides,
		filtersLoaded,
		// fetchDeadheadByIdWithFallback,
	]);

	const tabs = () => {
		const tabKeys = [...tabsMods.keys()];
		return tabKeys.map((tab) => (
			<TabItem
				key={tab}
				id={tabsMods.get(tab).tabId}
				panelId={tabsMods.get(tab).panelId}
				active={tabsMods.get(tab).tabId === searchMode}
				extraClasses={`${styles.tab} ${rideFilterStore.isRideIdApplied ? styles.disabled : ''}`}
			>
				{tabsMods.get(tab).label}
			</TabItem>
		));
	};

	const panels = () => {
		const tabKeys = [...TABS_MODS.keys()];
		return tabKeys.map((tab) => (
			<TabPanel
				key={TABS_MODS.get(tab).panelId}
				id={TABS_MODS.get(tab).panelId}
				tabId={TABS_MODS.get(tab).tabId}
			>
				{renderRides()}
			</TabPanel>
		));
	};

	const renderRides = () => {
		if (isRidesLoading) return <Loader />;
		if (isRidesError) return <NoData mode="error" />;
		if (!isRidesError && rides.length === 0) return <NoData mode="noresult" />;
		// const shouldShowDeadheadItem = isDeadheadsTabActive && !isShowingFallbackRides;
		// TODO: CLARIFY DEADHEAD SEARCH
		const shouldShowDeadheadItem = isDeadheadsTabActive;

		return rides.map((ride) =>
			shouldShowDeadheadItem ? (
				<DeadheadItem key={ride.id} timezone={selectedTimezone} rideDetails={ride} />
			) : (
				<RideItem key={ride.id} timezone={selectedTimezone} rideDetails={ride} />
			)
		);
	};

	return (
		<PageSizer>
			<div className={styles.header}>
				<div>
					<h1>Work assignment</h1>
					<p className={styles.title}>Here you can assign crew to rides</p>
				</div>

				<RideIdForm />
			</div>

			<div className={styles.filters}>
				<RideFilters />
			</div>

			<div className={styles.tabswrap}>
				<div className={`${styles.tabs} `}>
					<Tabs
						navRest={{
							Elem: 'section',
							'aria-label': 'Crew member details',
						}}
						tabs={tabs()}
						panels={panels()}
						onTabActivated={(tabId) => setSearchMode(tabId)}
					/>
				</div>

				<div className={styles.selector}>
					<TimezoneSelector
						onSelection={setSelectedTimezone}
						selected={selectedTimezone}
						additionalOptions={[TIME_ZONES_ADDITIONS.stationTime]}
					/>
				</div>
				<div className={styles.pagination}>
					<Grid align="center" justify="center">
						<GridCol inline>
							<Pagination
								activePage={activePage}
								setActivePage={setActivePage}
								pageCount={pageCount}
							/>
						</GridCol>
					</Grid>
				</div>
			</div>
		</PageSizer>
	);
});

export default RideListPage;
